import { Modal } from "@circleco/compass/components/Modal";
import { useCallback, useEffect, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";

import { CountdownScreen } from "./Compass/CountdownScreen";
import { RecordingScreen } from "./Compass/RecordingScreen";
import { SetupScreen } from "./Compass/SetupScreen";
import { VIDEO_CLIP_STEPS, type VideoClipStep } from "./constants";
import { useMediaStreams } from "./hooks/useMediaStreams";
import { useRecordingTimers } from "./hooks/useRecordingTimers";
import { useVideoClipSettingsStore } from "./hooks/useVideoClipSettingsStore";
import { useVideoClipStreams } from "./hooks/useVideoClipStreams";
import { useVideoRecorder } from "./hooks/useVideoRecorder";
import { LazyPreviewScreen } from "./LazyPreviewScreen";
import type { VideoClipModalProps } from "./types";
import { useVideoClipConfirmCloseDialog } from "./VideoClipConfirmCloseDialog";
import type { VideoClipContextValue } from "./VideoClipContext";
import { VideoClipContextProvider } from "./VideoClipContext";

interface VideoClipModalComponentProps extends VideoClipModalProps {
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export const VideoClipModalComponent = ({
  onInsert,
  open,
  onOpenChange,
}: VideoClipModalComponentProps) => {
  const confirmCloseModal = useVideoClipConfirmCloseDialog();
  const videoRef = useRef<HTMLVideoElement>(null);
  const cameraPreviewRef = useRef<HTMLVideoElement>(null);

  const {
    cameraDevices,
    cameraStream,
    cleanup,
    isCameraEnabled,
    isMicEnabled,
    isScreenSharing,
    micDevices,
    screenStream,
    startCamera,
    startScreenShare,
    stopScreenShare,
    toggleCamera,
    toggleMic,
  } = useMediaStreams();
  const {
    pipPosition,
    pipShape,
    pipSize,
    selectedCameraId,
    selectedMicId,
    setPipPosition,
    setPipShape,
    setPipSize,
    setSelectedCameraId,
    setSelectedMicId,
  } = useVideoClipSettingsStore();

  const [step, setStep] = useState<VideoClipStep>(VIDEO_CLIP_STEPS.SETUP);

  const handleToggleScreenShare = useCallback(() => {
    if (isScreenSharing) {
      stopScreenShare();
    } else {
      void startScreenShare();
    }
  }, [isScreenSharing, startScreenShare, stopScreenShare]);

  const { previewStream, recordingStream } = useVideoClipStreams({
    cameraStream,
    isMicEnabled,
    isScreenSharing,
    pipPosition,
    pipShape,
    pipSize,
    screenStream,
  });

  const handleRecordingStopped = useCallback(() => {
    setStep(VIDEO_CLIP_STEPS.PREVIEW);
    cleanup();
    if (videoRef.current) videoRef.current.srcObject = null;
    if (cameraPreviewRef.current) cameraPreviewRef.current.srcObject = null;
  }, [cleanup]);

  // Ref-based pattern to break the circular dependency between
  // useVideoRecorder (needs onRecordingError) and useRecordingTimers
  // (needs startRecording from useVideoRecorder).
  const onRecordingErrorRef = useRef<() => void>(() => {});

  const {
    clearRecording,
    recordedBlob,
    recordedUrl,
    startRecording,
    stopRecording,
  } = useVideoRecorder({
    stream: recordingStream,
    onRecordingError: useCallback(() => onRecordingErrorRef.current(), []),
    onRecordingStopped: handleRecordingStopped,
  });

  const handleCountdownComplete = useCallback(() => {
    startRecording();
    setStep(VIDEO_CLIP_STEPS.RECORDING);
  }, [startRecording]);

  const { countdownValue, elapsedTime, resetCountdown, resetElapsedTime } =
    useRecordingTimers({ step, onCountdownComplete: handleCountdownComplete });

  const resetToSetupAndRestartCamera = useCallback(() => {
    resetElapsedTime();
    setStep(VIDEO_CLIP_STEPS.SETUP);
    cleanup();
    if (videoRef.current) videoRef.current.srcObject = null;
    if (cameraPreviewRef.current) cameraPreviewRef.current.srcObject = null;
    void startCamera({
      cameraDeviceId: selectedCameraId,
      micDeviceId: selectedMicId,
    });
  }, [cleanup, resetElapsedTime, selectedCameraId, selectedMicId, startCamera]);

  // Keep the ref up to date so the stable callback passed to useVideoRecorder
  // always calls the latest version of resetToSetupAndRestartCamera.
  onRecordingErrorRef.current = resetToSetupAndRestartCamera;

  useEffect(() => {
    void startCamera({
      cameraDeviceId: selectedCameraId,
      micDeviceId: selectedMicId,
    });
  }, [startCamera, selectedCameraId, selectedMicId]);

  useEffect(() => {
    if (!videoRef.current) return;
    videoRef.current.srcObject = previewStream ?? null;
  }, [previewStream, step]);

  useEffect(() => {
    if (!cameraPreviewRef.current) return;
    cameraPreviewRef.current.srcObject =
      isScreenSharing && cameraStream ? cameraStream : null;
  }, [cameraStream, isScreenSharing, step]);

  const handleStartRecording = useCallback(() => {
    setStep(VIDEO_CLIP_STEPS.COUNTDOWN);
    resetCountdown();
  }, [resetCountdown]);

  const handleUseVideo = useCallback(() => {
    if (!recordedBlob) return;
    const extension = recordedBlob.type.includes("mp4") ? "mp4" : "webm";
    const file = new File(
      [recordedBlob],
      `video-clip-${Date.now()}.${extension}`,
      { type: recordedBlob.type },
    );
    onInsert(file);
    cleanup();
    clearRecording();
    onOpenChange(false);
  }, [recordedBlob, onInsert, cleanup, clearRecording, onOpenChange]);

  const handleStartOver = useCallback(() => {
    void confirmCloseModal.show({
      onConfirm: () => {
        clearRecording();
        resetToSetupAndRestartCamera();
      },
    });
  }, [confirmCloseModal, clearRecording, resetToSetupAndRestartCamera]);

  const handleClose = useCallback(() => {
    cleanup();
    clearRecording();
    onOpenChange(false);
  }, [cleanup, clearRecording, onOpenChange]);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        if (step !== VIDEO_CLIP_STEPS.SETUP) {
          void confirmCloseModal.show({ onConfirm: handleClose });
        } else {
          handleClose();
        }
      }
    },
    [step, handleClose, confirmCloseModal],
  );

  const isStartDisabled = !isCameraEnabled && !isScreenSharing;

  return (
    <Modal.Root open={open} onOpenChange={handleOpenChange} size="lg">
      <Modal.Content>
        <Modal.Header title={t("tiptap.record_video_clip")} />
        <Modal.Body>
          <VideoClipContextProvider
            value={
              {
                cameraPreviewRef,
                isCameraEnabled,
                isScreenSharing,
                pipPosition,
                pipShape,
                pipSize,
                videoRef,
              } as VideoClipContextValue
            }
          >
            {step === VIDEO_CLIP_STEPS.SETUP && (
              <SetupScreen
                cameraDevices={cameraDevices}
                isMicEnabled={isMicEnabled}
                micDevices={micDevices}
                onSelectCamera={setSelectedCameraId}
                onSelectMic={setSelectedMicId}
                onSelectPipPosition={setPipPosition}
                onSelectPipShape={setPipShape}
                onSelectPipSize={setPipSize}
                onToggleCamera={toggleCamera}
                onToggleMic={toggleMic}
                onToggleScreenShare={handleToggleScreenShare}
                selectedCameraId={selectedCameraId}
                selectedMicId={selectedMicId}
              />
            )}
            {step === VIDEO_CLIP_STEPS.COUNTDOWN && (
              <CountdownScreen count={countdownValue} />
            )}
            {step === VIDEO_CLIP_STEPS.RECORDING && (
              <RecordingScreen elapsedTime={elapsedTime} />
            )}
            {step === VIDEO_CLIP_STEPS.PREVIEW &&
              recordedUrl &&
              recordedBlob && (
                <LazyPreviewScreen
                  videoUrl={recordedUrl}
                  contentType={recordedBlob.type}
                />
              )}
          </VideoClipContextProvider>
        </Modal.Body>
        {step === VIDEO_CLIP_STEPS.SETUP && (
          <Modal.Footer
            secondaryAction={{ label: t("cancel"), onClick: handleClose }}
            primaryAction={{
              label: t("tiptap.video_clip.start_recording"),
              onClick: handleStartRecording,
              disabled: isStartDisabled,
            }}
          />
        )}
        {step === VIDEO_CLIP_STEPS.RECORDING && (
          <Modal.Footer
            primaryAction={{
              label: t("tiptap.video_clip.stop_recording"),
              onClick: stopRecording,
            }}
          />
        )}
        {step === VIDEO_CLIP_STEPS.PREVIEW && (
          <Modal.Footer
            secondaryAction={{
              label: t("tiptap.video_clip.start_over"),
              onClick: handleStartOver,
              shouldCloseModal: false,
            }}
            primaryAction={{
              label: t("tiptap.video_clip.use_video"),
              onClick: handleUseVideo,
            }}
          />
        )}
      </Modal.Content>
    </Modal.Root>
  );
};
